import { Controller } from '@hotwired/stimulus';

// /google-maps-api-pricing: monthly cost per Google Maps SKU, all in the browser.
// Prices come from GoogleMapsPricing via data-maps-pricing-data-value; the first row is
// server-rendered and cloned for every row added after it.
export default class extends Controller {
  static targets = ['rows', 'row', 'total', 'summary'];
  static values = { data: Object };

  connect() {
    this.skus = Object.fromEntries(this.dataValue.skus.map((sku) => [sku.id, sku]));
    this.bands = this.dataValue.bands;
    this.blank = this.rowTargets[0].cloneNode(true);
    this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    this.update();
  }

  add(event, id = 'dynamic-maps', count = 10000) {
    const row = this.blank.cloneNode(true);
    row.querySelector('[data-role="sku"]').value = id;
    row.querySelector('[data-role="count"]').value = count.toLocaleString('en-US');
    this.rowsTarget.appendChild(row);
    this.update();
  }

  remove(event) {
    if (this.rowTargets.length === 1) return;
    event.currentTarget.closest('[data-maps-pricing-target="row"]').remove();
    this.update();
  }

  preset(event) {
    const rows = JSON.parse(event.currentTarget.dataset.rows);
    this.rowTargets.forEach((row) => row.remove());
    rows.forEach(([id, count]) => this.add(null, id, count));
  }

  update() {
    let total = 0;
    let requests = 0;
    this.rowTargets.forEach((row) => {
      const sku = this.skus[row.querySelector('[data-role="sku"]').value];
      const count = this.parseCount(row.querySelector('[data-role="count"]').value);
      const cost = this.cost(sku, count);
      total += cost;
      requests += count;
      row.querySelector('[data-role="cost"]').textContent = this.format(cost);
      row.querySelector('[data-role="note"]').textContent = this.note(sku, count);
    });
    this.totalTarget.textContent = this.format(total);
    this.summaryTarget.textContent = `${requests.toLocaleString('en-US')} requests a month. Before tax. Free caps reset on the 1st of every month.`;
  }

  // Same math as GoogleMapsPricing.monthly_cost: free cap first, then each band at its price.
  cost(sku, requests) {
    if (!sku.prices || sku.free === null) return 0;
    let lower = 0;
    let total = 0;
    this.bands.forEach((upper, i) => {
      const from = Math.max(lower, sku.free);
      const to = upper === null ? requests : Math.min(requests, upper);
      if (to > from) total += ((to - from) * sku.prices[i]) / 1000;
      lower = upper === null ? requests : upper;
    });
    return total;
  }

  note(sku, count) {
    if (!sku.prices || sku.free === null) return 'Free with no monthly cap.';
    const free = sku.free.toLocaleString('en-US');
    const price = this.money.format(sku.prices[0]);
    if (count <= sku.free) return `Free: you're inside the ${free} free requests a month.`;
    return `First ${free} free, then ${price} per 1,000 (cheaper past 100,000).`;
  }

  parseCount(text) {
    const n = Math.floor(Number(String(text).replace(/[^0-9.]/g, '')));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  format(amount) {
    return amount === 0 ? '$0' : this.money.format(amount);
  }
}
